import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

export default function Customers() {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (showInactive) params.set('include_inactive', 'true');

      const data = await api.get(`/customers?${params.toString()}`, token);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [token, showInactive]);

  const setCustomerActive = async (customer, isActive) => {
    try {
      setSavingId(customer.id);
      setError('');
      setStatus('');

      await api.patch(`/customers/${customer.id}`, { is_active: isActive }, token);

      setCustomers((prev) =>
        prev.map((item) =>
          item.id === customer.id ? { ...item, is_active: isActive } : item
        )
      );

      setStatus(
        isActive
          ? `${customer.email} reactivated.`
          : `${customer.email} archived. Order history is preserved.`
      );
    } catch (err) {
      setError(err.message || 'Failed to update customer');
    } finally {
      setSavingId(null);
      setConfirmModal(null);
    }
  };

  const requestArchiveCustomer = (customer) => {
    setConfirmModal({
      title: 'Archive customer?',
      message: `${customer.email} will be hidden from the default customer list. Orders and purchase history remain intact.`,
      confirmLabel: 'Archive customer',
      destructive: true,
      onConfirm: () => setCustomerActive(customer, false)
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
        <p className="mt-1 text-sm text-slate-500">Customer records and purchase history overview.</p>
      </div>

      <div className="card p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <input
            className="input-field min-h-[44px]"
            placeholder="Search email or name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                loadCustomers();
              }
            }}
          />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            Show archived customers
          </label>
        </div>

        <button className="btn-primary" onClick={loadCustomers}>
          Search
        </button>
      </div>

      {error ? <div className="status-banner-error">{error}</div> : null}
      {status ? <div className="status-banner-success">{status}</div> : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Orders</th>
              <th>Total Spent</th>
              <th>Joined</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id}>
                <td>{customer.email}</td>
                <td>{`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '—'}</td>
                <td>{customer.order_count}</td>
                <td>{money(customer.total_spent)}</td>
                <td>{new Date(customer.created_at).toLocaleDateString()}</td>
                <td>
                  {customer.is_active === false ? (
                    <span className="badge badge-red">Archived</span>
                  ) : (
                    <span className="badge badge-green">Active</span>
                  )}
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    {customer.is_active === false ? (
                      <button
                        className="btn-secondary px-3 py-2 text-xs"
                        disabled={savingId === customer.id}
                        onClick={() => setCustomerActive(customer, true)}
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        className="btn-danger-soft px-3 py-2 text-xs"
                        disabled={savingId === customer.id}
                        onClick={() => requestArchiveCustomer(customer)}
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {!customers.length && !loading ? (
              <tr><td colSpan="7">No customers found.</td></tr>
            ) : null}

            {loading ? (
              <tr><td colSpan="7">Loading customers...</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        destructive={confirmModal?.destructive}
        loading={Boolean(savingId)}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}
